import React, { useEffect, useState } from 'react';
import '../css/accounting.css';

const currentMonthKey = () => new Date().toISOString().slice(0, 7);

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

function Income() {
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [monthlyBonus, setMonthlyBonus] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [updatedAt, setUpdatedAt] = useState(null);
  const [sourceMonth, setSourceMonth] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [accountsStatusMessage, setAccountsStatusMessage] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState('savings');
  const [accountBalance, setAccountBalance] = useState('0');

  const user = localStorage.getItem('loggedInUsername');

  const loadIncome = async (token, monthKey) => {
    const response = await fetch('/api/accounting/income', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user, month_key: monthKey })
    });

    if (!response.ok) {
      throw new Error('Failed to load income');
    }

    return response.json();
  };

  const loadAccounts = async (token) => {
    const response = await fetch('/api/accounting/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load accounts');
    }

    return response.json();
  };

  const refreshAccounts = async (token) => {
    try {
      const data = await loadAccounts(token);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (error) {
      setAccountsStatusMessage('Unable to load accounts.');
    }
  };

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return;
      }
      try {
        setStatusMessage('');
        const [incomeData, accountsData] = await Promise.all([
          loadIncome(token, selectedMonth),
          loadAccounts(token)
        ]);
        setMonthlyIncome(incomeData.monthly_income || 0);
        setMonthlyBonus(incomeData.monthly_bonus || 0);
        setUpdatedAt(incomeData.updated_at || null);
        setSourceMonth(incomeData.source_month || '');
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
      } catch (error) {
        setStatusMessage('Unable to load income settings.');
      }
    };

    load();
  }, [selectedMonth]);

  const handleSave = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/income/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          month_key: selectedMonth,
          monthly_income: monthlyIncome,
          monthly_bonus: monthlyBonus
        })
      });

      const data = await response.json();

      if (response.ok) {
        const now = Math.floor(Date.now() / 1000);
        setUpdatedAt(now);
        setSourceMonth(selectedMonth);
        setStatusMessage(data?.message || 'Income settings saved.');
      } else {
        setStatusMessage(data?.error || 'Unable to save income settings.');
      }
    } catch (error) {
      setStatusMessage('Unable to save income settings.');
    }
  };

  const handleAddAccount = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    if (!String(accountName || '').trim()) {
      setAccountsStatusMessage('Account name is required.');
      return;
    }

    try {
      const response = await fetch('/api/accounting/accounts/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          name: accountName,
          account_type: accountType,
          balance: accountBalance
        })
      });

      const data = await response.json();
      if (!response.ok) {
        setAccountsStatusMessage(data?.error || 'Unable to add account.');
        return;
      }

      setAccountName('');
      setAccountType('savings');
      setAccountBalance('0');
      setAccountsStatusMessage('Account added.');
      await refreshAccounts(token);
    } catch (error) {
      setAccountsStatusMessage('Unable to add account.');
    }
  };

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Income Settings</h2>
            <p className="section-subtitle">Keep your baseline income and bonus cadence up to date.</p>
          </div>
        </div>

        <div className="stacked-panels">
          <div className="panel">
            <h3>Monthly Income</h3>
            <p className="panel-sub">Used for forecasting and net calculations.</p>
            <div className="form-grid">
              <div className="form-row">
                <label>
                  Year / month
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </label>
                <label>
                  Base income
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(e.target.value)}
                  />
                </label>
                <label>
                  Bonus income
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyBonus}
                    onChange={(e) => setMonthlyBonus(e.target.value)}
                  />
                </label>
              </div>
              <div className="form-row">
                <button className="login-button" type="button" onClick={handleSave}>Save income</button>
                <span className="badge">Total: {formatCurrency(Number(monthlyIncome || 0) + Number(monthlyBonus || 0))}</span>
              </div>
              <span className="section-subtitle">
                Changes apply from {selectedMonth || 'this month'} onward until a future month is updated.
              </span>
              {sourceMonth && sourceMonth !== selectedMonth && (
                <span className="section-subtitle">Using values inherited from {sourceMonth}.</span>
              )}
              {updatedAt && (
                <span className="section-subtitle">Last updated {new Date(updatedAt * 1000).toLocaleString()}</span>
              )}
              {statusMessage && <span className="section-subtitle">{statusMessage}</span>}
            </div>
          </div>

          <div className="panel">
            <h3>Accounts</h3>
            <p className="panel-sub">Add additional accounts without leaving Income Settings.</p>
            <div className="form-grid">
              <div className="form-row">
                <label>
                  Account name
                  <input
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="e.g. Holiday fund"
                  />
                </label>
                <label>
                  Account type
                  <select value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                    <option value="current">Current</option>
                    <option value="savings">Savings</option>
                    <option value="investment">Investment</option>
                  </select>
                </label>
                <label>
                  Starting balance
                  <input
                    type="number"
                    step="0.01"
                    value={accountBalance}
                    onChange={(e) => setAccountBalance(e.target.value)}
                  />
                </label>
              </div>
              <div className="form-row">
                <button className="login-button" type="button" onClick={handleAddAccount}>Add account</button>
              </div>
              {accountsStatusMessage && <span className="section-subtitle">{accountsStatusMessage}</span>}
            </div>

            {accounts.length === 0 ? (
              <div className="empty-state">No accounts found.</div>
            ) : (
              <table className="table" style={{ marginTop: '1rem' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id}>
                      <td>{account.name}</td>
                      <td>{account.account_type}</td>
                      <td>{formatCurrency(account.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Income;
